import React, { useMemo, useCallback } from 'react';
import { faUserPlus, faRefresh, faTrash } from '@fortawesome/free-solid-svg-icons';
import ActionToolbar from '../common/ActionToolbar';
import { useTranslation } from 'react-i18next';
import { useRole } from '../../context/roleContext';
import { USER_ROLES } from '../../utils/constants';

const VISIBLE_STATUSES = ['pending', 'confirmed', 'partial_advice_created'];

/**
 * PurchaseOrdersActions
 * A simplified actions toolbar for Purchase Orders: search, status filter, add and refresh.
 */
const PurchaseOrdersActions = ({
    showSearch = true,
    searchValue = '',
    onSearchChange,
    onAddOrder,
    onRefresh,
    loading = false,
    refreshLoading = false,
    onDeleteAll,
    onBulkDelete,
    hasSelection = false,
    selectionCount = 0,
    statusValue = '',
    onStatusChange,
    fromDate = '',
    toDate = '',
    onFromDateChange,
    onToDateChange,
}) => {
    const { t } = useTranslation();
    const handleAdd = useCallback(() => {
        onAddOrder?.();
    }, [onAddOrder]);

    const { userRole, checkDeleteAccess } = useRole();

    const actions = useMemo(() => {
        const baseActions = [
            {
                key: 'refresh-orders',
                label: t('purchaseOrdersModule.common.buttons.refresh'),
                icon: faRefresh,
                onClick: onRefresh,
                disabled: refreshLoading,
                variant: 'ghost',
                showLabelBreakpoint: 'sm',
                spinner: refreshLoading,
            }
        ];

        // Hide Add Order for DELIVERY_SUPERVISOR and SALES_STAFF
        const restrictedRoles = [USER_ROLES.DELIVERY_SUPERVISOR];
        if (!restrictedRoles.includes(userRole)) {
            baseActions.push({
                key: 'add-order',
                label: t('purchaseOrdersModule.purchaseOrders.actions.buttons.add'),
                icon: faUserPlus,
                onClick: handleAdd,
                disabled: loading,
                variant: 'primary',
                showLabelBreakpoint: 'sm',
                loading,
            });
        }

        if (hasSelection && userRole === USER_ROLES.SUPER_ADMIN && checkDeleteAccess()) {
            baseActions.push({
                key: 'bulk-delete',
                label: `Bulk Delete (${selectionCount})`,
                icon: faTrash,
                onClick: onBulkDelete,
                disabled: loading,
                variant: 'danger',
                showLabelBreakpoint: 'sm',
                loading,
            });
        }

        if (userRole === USER_ROLES.SUPER_ADMIN && checkDeleteAccess()) {
            baseActions.push({
                key: 'delete-all',
                tooltip: 'Super Delete All',
                icon: faTrash,
                onClick: onDeleteAll,
                disabled: loading,
                variant: 'danger',
                className: '!px-2 !py-1.5 !text-[10px]',
                loading,
            });
        }


        return baseActions;
    }, [handleAdd, loading, onRefresh, refreshLoading, userRole, checkDeleteAccess, onDeleteAll, onBulkDelete, hasSelection, selectionCount, t]);

    const statusOptions = useMemo(() => {
        const opts = [{ value: '', label: t('purchaseOrdersModule.purchaseOrders.actions.filters.all') }];
        const statuses = t('purchaseOrdersModule.purchaseOrders.statuses', { returnObjects: true });
        VISIBLE_STATUSES.forEach((k) => {
            if (statuses[k]) opts.push({ value: k, label: statuses[k] });
        });
        return opts;
    }, [t]);

    const filters = useMemo(() => ([
        {
            key: 'from-date',
            type: 'date',
            label: t('commonCols.from'),
            value: fromDate,
            onChange: onFromDateChange,
        },
        {
            key: 'to-date',
            type: 'date',
            label: 'To',
            value: toDate,
            onChange: onToDateChange,
        },
        {
            key: 'status-filter',
            type: 'select',
            label: t('purchaseOrdersModule.purchaseOrders.actions.filters.status'),
            value: statusValue,
            options: statusOptions,
            onChange: onStatusChange,
        },
    ]), [statusValue, onStatusChange, statusOptions, fromDate, toDate, onFromDateChange, onToDateChange, t]);

    return (
        <ActionToolbar
            showSearch={showSearch}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            searchPlaceholder={t('purchaseOrdersModule.purchaseOrders.actions.searchPlaceholder')}
            filters={filters}
            actions={actions}
        />
    );
};

export default PurchaseOrdersActions;
